/**
 * Fuselage builder — cylinder body + cone nose + tapered tail
 *
 * Orientation: fuselage lies along Z axis, nose at +Z, tail at -Z.
 * Origin: center of fuselage cylinder (Y = 0 is centerline).
 */

import * as THREE from 'three'
import type { CategoryProfile } from '../types'

const RADIAL_SEGMENTS = 8 // "wooden toy" look

export function createFuselage(
  fuselageLength: number,
  fuselageRadius: number,
  noseLength: number,
  tailTaperLength: number,
  _profile: CategoryProfile,
  material: THREE.MeshStandardMaterial
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'fuselage'

  // Main cylinder body (along Z axis)
  const bodyLength = fuselageLength - noseLength - tailTaperLength
  const bodyGeo = new THREE.CylinderGeometry(
    fuselageRadius, fuselageRadius, bodyLength, RADIAL_SEGMENTS
  )
  const body = new THREE.Mesh(bodyGeo, material)
  // CylinderGeometry is vertical (Y-axis) by default, rotate to lie along Z
  body.rotation.x = Math.PI / 2
  // Center the body between nose and tail
  body.position.z = (noseLength - tailTaperLength) / 2
  body.castShadow = true
  body.receiveShadow = true
  group.add(body)

  // Nose cone (points toward +Z)
  const noseGeo = new THREE.ConeGeometry(fuselageRadius, noseLength, RADIAL_SEGMENTS)
  const nose = new THREE.Mesh(noseGeo, material)
  // Cone points up by default; rotate so tip points +Z (forward)
  // +Y (tip) rotates to +Z, -Y (base) rotates to -Z (attaches to fuselage)
  nose.rotation.x = Math.PI / 2
  nose.position.z = (fuselageLength - noseLength) / 2
  nose.castShadow = true
  group.add(nose)

  // Tail taper (points toward -Z, reducing to ~40% radius)
  if (tailTaperLength > 0) {
    const tailGeo = new THREE.CylinderGeometry(
      fuselageRadius * 0.4, fuselageRadius, tailTaperLength, RADIAL_SEGMENTS
    )
    const tail = new THREE.Mesh(tailGeo, material)
    // CylinderGeometry: radiusTop (small) at +Y, radiusBottom (large) at -Y
    // Rotate so small end at -Z (backward), large end at +Z (attaches to fuselage)
    tail.rotation.x = -Math.PI / 2
    tail.position.z = -(fuselageLength - tailTaperLength) / 2
    tail.castShadow = true
    group.add(tail)
  }

  return group
}
